import { openDB, type IDBPDatabase } from 'idb'
import type { TechnicianWorkOrderResponseDto } from '../types/workOrder'
import type { TechnicianDayDestination } from './workOrderService'

interface PendingWorkOrderUpdate {
  id: string
  workOrderId: number
  type: 'workorder' | 'alarm' | 'note' | 'photo'
  action: 'create' | 'update' | 'delete'
  data: Record<string, unknown>
  timestamp: number
  status: 'pending' | 'syncing' | 'synced' | 'failed'
  retryCount: number
  error?: string
  [key: string]: unknown // Index signature for compatibility
}

interface OfflineWorkOrder {
  workOrderId: number
  workOrderResponseData: TechnicianWorkOrderResponseDto // Store the complete server response
  lastModified: number
  isOffline: boolean
  hasPendingChanges: boolean
  lastSyncTime?: number
  version?: number // For conflict detection
}

interface OfflineDayDestinations {
  cacheKey: string // technicianId-date format
  destinations: TechnicianDayDestination[]
  lastModified: number
  lastSyncTime?: number
}

export interface FileData {
  id?: number
  name: string
  // Store the raw bytes (read at capture time) rather than a File reference.
  // On iOS a File from an input is a reference to an OS-managed temp file whose
  // backing data gets evicted; storing the ArrayBuffer keeps the bytes inline in
  // the IndexedDB record so they survive offline queuing and app reopens.
  data: ArrayBuffer
  fileType: string
  size: number
  lastModified: number
  workOrderId: number
  alarmId?: number | string
  type: 'workorder' | 'alarm'
  status: 'pending' | 'uploading' | 'uploaded' | 'failed'
  timestamp: number
  error?: string
}

interface OfflineWorkOrderDB {
  pendingUpdates: {
    key: string
    value: PendingWorkOrderUpdate
    indexes: { workOrderId: number; status: string; type: string }
  }
  workOrders: {
    key: number
    value: OfflineWorkOrder
    indexes: { lastModified: number }
  }
  dayDestinations: {
    key: string
    value: OfflineDayDestinations
    indexes: { lastModified: number }
  }
  files: {
    key: number
    value: FileData
    indexes: { workOrderId: number; status: string }
  }
}

class OfflineStorageService {
  private db: IDBPDatabase<OfflineWorkOrderDB> | null = null
  private dbName = 'coastsmokeapp-offline'
  private version = 4
  private isInitializing = false

  async initWorkOrderDB(): Promise<void> {
    // Prevent multiple concurrent initializations
    if (this.isInitializing) {
      while (this.isInitializing) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      return
    }

    if (this.db) {
      return // Already initialized
    }

    this.isInitializing = true
    
    try {
      this.db = await openDB<OfflineWorkOrderDB>(this.dbName, this.version, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        // v3: FileData now stores raw bytes (ArrayBuffer) instead of a File
        // reference. Any records queued under the old schema hold dead File refs
        // whose backing bytes iOS has already evicted - they are unrecoverable
        // and would only fail forever, so clear them.
        if (oldVersion >= 1 && oldVersion < 3 && db.objectStoreNames.contains('files')) {
          transaction.objectStore('files').clear()
        }

        // Pending updates store
        if (!db.objectStoreNames.contains('pendingUpdates')) {
          const store = db.createObjectStore('pendingUpdates', { keyPath: 'id' })
          store.createIndex('workOrderId', 'workOrderId', { unique: false })
          store.createIndex('status', 'status', { unique: false })
          store.createIndex('type', 'type', { unique: false })
        }
        
        // Offline work orders store
        if (!db.objectStoreNames.contains('workOrders')) {
          const store = db.createObjectStore('workOrders', { keyPath: 'workOrderId' })
          store.createIndex('lastModified', 'lastModified', { unique: false })
        }
        
        // Offline day destinations store
        if (!db.objectStoreNames.contains('dayDestinations')) {
          const store = db.createObjectStore('dayDestinations', { keyPath: 'cacheKey' })
          store.createIndex('lastModified', 'lastModified', { unique: false })
        }
        
        // Offline files store (similar to PWA demo)
        if (!db.objectStoreNames.contains('files')) {
          const store = db.createObjectStore('files', { keyPath: 'id', autoIncrement: true })
          store.createIndex('workOrderId', 'workOrderId', { unique: false })
          store.createIndex('status', 'status', { unique: false })
        }
      }
    })

      // v4 cleanup: v3 retained the full bytes after successful uploads. Remove
      // those records and recover uploads interrupted by an app close/crash,
      // while preserving the durable photo bytes needed for iOS retries.
      await this.recoverFileQueue()
    
    } catch (error) {
      this.db = null
      throw error
    } finally {
      this.isInitializing = false
    }
  }

  private generateId(): string {
    return Date.now().toString() + Math.random().toString(36).substring(2, 11)
  }

  // Save pending update for later sync
  async savePendingUpdate(
    workOrderId: number,
    type: PendingWorkOrderUpdate['type'],
    action: PendingWorkOrderUpdate['action'],
    data: Record<string, unknown>
  ): Promise<string> {
    if (!this.db) await this.initWorkOrderDB()

    const update: PendingWorkOrderUpdate = {
      id: this.generateId(),
      workOrderId,
      type,
      action,
      data,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0
    }

    await this.db!.add('pendingUpdates', update)
    return update.id
  }

  // Get all pending updates
  async getPendingUpdates(): Promise<PendingWorkOrderUpdate[]> {
    if (!this.db) await this.initWorkOrderDB()
    return await this.db!.getAll('pendingUpdates')
  }

  // Get pending updates for specific work order
  async getPendingUpdatesForWorkOrder(workOrderId: number): Promise<PendingWorkOrderUpdate[]> {
    if (!this.db) await this.initWorkOrderDB()
    const tx = this.db!.transaction('pendingUpdates', 'readonly')
    const index = tx.store.index('workOrderId')
    const updates = []
    
    for await (const cursor of index.iterate(workOrderId)) {
      if (cursor.value.status !== 'synced') {
        updates.push(cursor.value)
      }
    }
    
    return updates
  }

  // Check if work order has pending changes
  async hasPendingChanges(workOrderId: number): Promise<boolean> {
    const pending = await this.getPendingUpdatesForWorkOrder(workOrderId)
    return pending.length > 0
  }

  // Update pending update status
  async updatePendingUpdateStatus(
    id: string, 
    status: PendingWorkOrderUpdate['status'], 
    error?: string
  ): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    const update = await this.db!.get('pendingUpdates', id)
    if (update) {
      update.status = status
      if (error) update.error = error
      if (status === 'failed') update.retryCount++
      await this.db!.put('pendingUpdates', update)
    }
  }

  // Remove completed updates
  async cleanupPendingUpdates(): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    const tx = this.db!.transaction('pendingUpdates', 'readwrite')
    const index = tx.store.index('status')
    
    for await (const cursor of index.iterate('synced')) {
      cursor.delete()
    }
    
    await tx.done
  }

  // Save work order offline (from server - clean state)
  async saveWorkOrderOffline(workOrderId: number, data: TechnicianWorkOrderResponseDto, fromServer = false): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()

    const existing = await this.getWorkOrderOffline(workOrderId)
    const hasPending = await this.hasPendingChanges(workOrderId)
    
    if (fromServer && existing && hasPending) {
      return; // Don't overwrite with server data if there are pending changes
    }
    
    const offlineWorkOrder: OfflineWorkOrder = {
      workOrderId,
      workOrderResponseData: data as TechnicianWorkOrderResponseDto, 
      lastModified: Date.now(),
      isOffline: !fromServer,
      hasPendingChanges: fromServer ? false : (existing?.hasPendingChanges || hasPending),
      lastSyncTime: fromServer ? Date.now() : existing?.lastSyncTime
    }

    await this.db!.put('workOrders', offlineWorkOrder)
  }

  // Get offline work order
  async getWorkOrderOffline(workOrderId: number): Promise<OfflineWorkOrder | null> {
    if (!this.db) await this.initWorkOrderDB()
    return (await this.db!.get('workOrders', workOrderId)) || null
  }

  // Clear all pending changes for a work order (after successful sync)
  async clearPendingChanges(workOrderId: number): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    // Remove all pending updates for this work order
    const tx = this.db!.transaction('pendingUpdates', 'readwrite')
    const index = tx.store.index('workOrderId')
    
    for await (const cursor of index.iterate(workOrderId)) {
      cursor.delete()
    }
    
    await tx.done

    // Update work order state
    const workOrder = await this.getWorkOrderOffline(workOrderId)
    if (workOrder) {
      workOrder.hasPendingChanges = false
      workOrder.lastSyncTime = Date.now()
      await this.db!.put('workOrders', workOrder)
    }
  }

  // Get work order state summary
  async getWorkOrderState(workOrderId: number): Promise<{
    exists: boolean
    isOffline: boolean
    hasPendingChanges: boolean
    lastModified?: number
    lastSyncTime?: number
    pendingCount: number
  }> {
    const workOrder = await this.getWorkOrderOffline(workOrderId)
    const pendingUpdates = await this.getPendingUpdatesForWorkOrder(workOrderId)
    
    return {
      exists: !!workOrder,
      isOffline: workOrder?.isOffline || false,
      hasPendingChanges: workOrder?.hasPendingChanges || pendingUpdates.length > 0,
      lastModified: workOrder?.lastModified,
      lastSyncTime: workOrder?.lastSyncTime,
      pendingCount: pendingUpdates.length
    }
  }

  // Get filtered work orders from offline storage
  async getFilteredWorkOrdersOffline(technicianId: number, date: string, statusFilter: string): Promise<Record<string, unknown>[]> {
    if (!this.db) await this.initWorkOrderDB()
    
    const tx = this.db!.transaction('workOrders', 'readonly')
    const allWorkOrders = await tx.store.getAll()
    
    
    // Filter work orders based on criteria
    const filteredWorkOrders = allWorkOrders.filter(wo => {
      // Access the workOrder from the stored response data
      const workOrderData = wo.workOrderResponseData?.workOrder || {}
      
      // Extract technician ID from various possible locations
      let workOrderTechnicianId = workOrderData.technicianId || 
                                  workOrderData.techUserID_FK || 
                                  workOrderData.technicianID_FK ||
                                  workOrderData.technicianUserId
      
      // If this is detailed work order data, the technician ID might be nested differently
      if (!workOrderTechnicianId && workOrderData.workOrder) {
        workOrderTechnicianId = workOrderData.workOrder.technicianId || 
                               workOrderData.workOrder.techUserID_FK ||
                               workOrderData.workOrder.technicianID_FK ||
                               workOrderData.workOrder.technicianUserId
      }
      
      
      // Filter by technician
      if (workOrderTechnicianId !== technicianId) {
        return false
      }
      
      // Filter by date - check various possible field names and nested structures
      let scheduledDate = workOrderData.scheduledDate || 
                         workOrderData.woScheduledDate ||
                         workOrderData.woscheduledDate ||
                         workOrderData.date
      
      // If this is detailed work order data, the date might be nested differently
      if (!scheduledDate && workOrderData) {
        scheduledDate = workOrderData.scheduledDate || 
                       workOrderData.woScheduledDate ||
                       workOrderData.woscheduledDate ||
                       workOrderData.date
      }
      
      if (scheduledDate) {
        // Create date objects in local timezone to avoid timezone conversion issues
        const workOrderDateObj = new Date(scheduledDate)
        const workOrderDateStr = `${workOrderDateObj.getFullYear()}-${String(workOrderDateObj.getMonth() + 1).padStart(2, '0')}-${String(workOrderDateObj.getDate()).padStart(2, '0')}`
        
        if (workOrderDateStr !== date) {
          return false
        }
      }
      
      // Filter by status
      if (statusFilter !== 'all') {
        const statusMapping: { [key: string]: number } = {
          'booked': 1,
          'completed': 2,
          'cancelled': 3,
          'unableToComplete': 4
        }
        
        // Handle both numeric statusID_FK and string status fields, including nested structures
        let workOrderStatusId = workOrderData.statusID_FK
        let workOrderStatus = workOrderData.status?.toLowerCase()
        
        // If this is detailed work order data, check nested structure
        if (!workOrderStatusId && !workOrderStatus && workOrderData.workOrder) {
          workOrderStatusId = workOrderData.workOrder.statusID_FK
          workOrderStatus = workOrderData.workOrder.status?.toLowerCase()
        }
        
        const expectedStatusId = statusMapping[statusFilter]
        if (expectedStatusId) {
          // Check if it matches either the numeric ID or string status
          const matchesNumeric = workOrderStatusId === expectedStatusId
          const matchesString = workOrderStatus && (
            workOrderStatus.includes(statusFilter) || 
            statusFilter.includes(workOrderStatus)
          )
          
          if (!matchesNumeric && !matchesString) {
            return false
          }
        }
      }
      
      return true
    })
    
    
    // Return the work order data from the stored response
    return filteredWorkOrders.map(wo => {
      // Return just the workOrder part for list view
      return wo.workOrderResponseData.workOrder || {}
    }).filter(data => data)
  }

  // Save day destinations offline
  async saveDayDestinationsOffline(technicianId: number, date: string, destinations: TechnicianDayDestination[]): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    const cacheKey = `${technicianId}-${date}`
    const offlineDestinations: OfflineDayDestinations = {
      cacheKey,
      destinations,
      lastModified: Date.now(),
      lastSyncTime: Date.now()
    }
    
    await this.db!.put('dayDestinations', offlineDestinations)
  }

  // Get day destinations offline
  async getDayDestinationsOffline(technicianId: number, date: string): Promise<TechnicianDayDestination[]> {
    if (!this.db) await this.initWorkOrderDB()
    
    const cacheKey = `${technicianId}-${date}`
    const cached = await this.db!.get('dayDestinations', cacheKey)
    
    return cached?.destinations || []
  }

  // Clear all data from the database
  async clearDatabase(): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    
    try {
      // Clear all stores
      await Promise.all([
        this.db!.clear('workOrders'),
        this.db!.clear('pendingUpdates'),
        this.db!.clear('dayDestinations'),
        this.db!.clear('files')
      ])
      
    } catch (error) {
      throw error
    }
  }

  // Clear specific work order data
  async clearWorkOrderData(workOrderId: number): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    
    try {
      // Remove work order
      await this.db!.delete('workOrders', workOrderId)
      
      // Remove all pending updates for this work order
      const tx = this.db!.transaction('pendingUpdates', 'readwrite')
      const index = tx.store.index('workOrderId')
      
      for await (const cursor of index.iterate(workOrderId)) {
        cursor.delete()
      }
      
      await tx.done
      
      // Remove all files for this work order
      const fileTx = this.db!.transaction('files', 'readwrite')
      const fileIndex = fileTx.store.index('workOrderId')
      
      for await (const cursor of fileIndex.iterate(workOrderId)) {
        cursor.delete()
      }
      
      await fileTx.done
      
    } catch (error) {
      throw error
    }
  }

  // Save file for later upload (similar to PWA demo)
  async saveFileForUpload(
    file: File, 
    workOrderId: number, 
    alarmId?: number | string,
    type: 'workorder' | 'alarm' = 'workorder'
  ): Promise<number> {
    if (!this.db) await this.initWorkOrderDB()

    // Read the bytes NOW, while the File from the input is still alive. Storing
    // the ArrayBuffer (by value) rather than the File reference keeps the photo
    // readable after offline queuing / app reopens on iOS.
    const data = await file.arrayBuffer()

    const fileData: FileData = {
      name: file.name,
      data,
      fileType: file.type,
      size: file.size,
      lastModified: file.lastModified,
      workOrderId,
      alarmId,
      type,
      status: 'pending',
      timestamp: Date.now()
    }

    const result = await this.db!.add('files', fileData)
    return result as number
  }

  // Get pending files
  async getPendingFiles(): Promise<FileData[]> {
    if (!this.db) await this.initWorkOrderDB()
    const [pendingFiles, failedFiles] = await Promise.all([
      this.db!.getAllFromIndex('files', 'status', 'pending'),
      this.db!.getAllFromIndex('files', 'status', 'failed')
    ])
    return [...pendingFiles, ...failedFiles]
  }

  private async recoverFileQueue(): Promise<void> {
    if (!this.db) return

    const tx = this.db.transaction('files', 'readwrite')
    const statusIndex = tx.store.index('status')

    for await (const cursor of statusIndex.iterate('uploaded')) {
      await cursor.delete()
    }

    // No upload can still be active when a new database connection is opened.
    // Reset interrupted records so normal sync can retry them instead of leaving
    // their image bytes stranded indefinitely.
    for await (const cursor of statusIndex.iterate('uploading')) {
      await cursor.update({
        ...cursor.value,
        status: 'pending',
        error: undefined
      })
    }

    await tx.done
  }

  // Update file status
  async updateFileStatus(id: number, status: FileData['status'], error?: string): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    const fileData = await this.db!.get('files', id)
    if (fileData) {
      fileData.status = status
      if (error) fileData.error = error
      await this.db!.put('files', fileData)
    }
  }

  // Get file by ID
  async getFileById(id: number): Promise<FileData | null> {
    if (!this.db) await this.initWorkOrderDB()
    const fileData = await this.db!.get('files', id)
    return fileData || null
  }

  // Delete a single file record
  async deleteFile(id: number): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    await this.db!.delete('files', id)
  }

  // Check if we're offline
  isOffline(): boolean {
    return !navigator.onLine
  }

  // Debug method to check storage health
  async getStorageStats(): Promise<{
    workOrders: number
    pendingUpdates: number
    dayDestinations: number
    files: number
    isInitialized: boolean
  }> {
    if (!this.db) await this.initWorkOrderDB()
    
    const [workOrders, pendingUpdates, dayDestinations, files] = await Promise.all([
      this.db!.count('workOrders'),
      this.db!.count('pendingUpdates'), 
      this.db!.count('dayDestinations'),
      this.db!.count('files')
    ])
    
    return {
      workOrders,
      pendingUpdates,
      dayDestinations,
      files,
      isInitialized: !!this.db
    }
  }

  // Get detailed pending updates with full information
  async getDetailedPendingUpdates(): Promise<{
    workOrderUpdates: Array<PendingWorkOrderUpdate & { workOrderNumber?: string }>
    failedCount: number
  }> {
    if (!this.db) await this.initWorkOrderDB()
    
    const pendingUpdates = await this.getPendingUpdates()
    
    // Enhance with work order numbers
    const enhancedUpdates = await Promise.all(
      pendingUpdates.map(async (update) => {
        const workOrder = await this.getWorkOrderOffline(update.workOrderId)
        return {
          ...update,
          workOrderNumber: workOrder?.workOrderResponseData?.workOrder?.intWONo || 
                          `WO-${update.workOrderId}`
        }
      })
    )
    
    const failedCount = enhancedUpdates.filter(u => u.status === 'failed').length
    
    return {
      workOrderUpdates: enhancedUpdates,
      failedCount
    }
  }

  // Get detailed pending files with enhanced information
  async getDetailedPendingFiles(): Promise<{
    files: Array<FileData & { workOrderNumber?: string }>
    failedCount: number
  }> {
    if (!this.db) await this.initWorkOrderDB()
    
    const pendingFiles = await this.getPendingFiles()
    
    // Enhance with work order numbers
    const enhancedFiles = await Promise.all(
      pendingFiles.map(async (file) => {
        const workOrder = await this.getWorkOrderOffline(file.workOrderId)
        return {
          ...file,
          workOrderNumber: workOrder?.workOrderResponseData?.workOrder?.intWONo || 
                          `WO-${file.workOrderId}`
        }
      })
    )
    
    const failedCount = enhancedFiles.filter(f => f.status === 'failed').length
    
    return {
      files: enhancedFiles,
      failedCount
    }
  }

  // Get count of all pending items by status
  async getPendingCounts(): Promise<{
    workOrderUpdates: { pending: number; failed: number }
    files: { pending: number; failed: number }
    total: number
  }> {
    if (!this.db) await this.initWorkOrderDB()
    
    const [updates, files] = await Promise.all([
      this.getPendingUpdates(),
      this.getPendingFiles()
    ])
    
    const workOrderUpdatesPending = updates.filter(u => u.status === 'pending' || u.status === 'syncing').length
    const workOrderUpdatesFailed = updates.filter(u => u.status === 'failed').length
    
    const filesPending = files.filter(f => f.status === 'pending' || f.status === 'uploading').length
    const filesFailed = files.filter(f => f.status === 'failed').length
    
    return {
      workOrderUpdates: {
        pending: workOrderUpdatesPending,
        failed: workOrderUpdatesFailed
      },
      files: {
        pending: filesPending,
        failed: filesFailed
      },
      total: workOrderUpdatesPending + filesPending
    }
  }

  // Retry a specific failed update
  async retryFailedUpdate(updateId: string): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    const update = await this.db!.get('pendingUpdates', updateId)
    if (update && update.status === 'failed') {
      update.status = 'pending'
      update.retryCount = 0
      update.error = undefined
      await this.db!.put('pendingUpdates', update)
    }
  }

  // Retry a specific failed file
  async retryFailedFile(fileId: number): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    const file = await this.db!.get('files', fileId)
    if (file && file.status === 'failed') {
      file.status = 'pending'
      file.error = undefined
      await this.db!.put('files', file)
    }
  }

  // Clear all failed items
  async clearFailedItems(): Promise<void> {
    if (!this.db) await this.initWorkOrderDB()
    
    const tx = this.db!.transaction(['pendingUpdates', 'files'], 'readwrite')
    
    // Get all failed updates
    const failedUpdates = await tx.objectStore('pendingUpdates').index('status').getAll('failed')
    for (const update of failedUpdates) {
      await tx.objectStore('pendingUpdates').delete(update.id)
    }
    
    // Get all failed files
    const failedFiles = await tx.objectStore('files').index('status').getAll('failed')
    for (const file of failedFiles) {
      if (file.id) {
        await tx.objectStore('files').delete(file.id)
      }
    }
    
    await tx.done
  }
}

// Keep the existing OfflineDB interface and methods for backward compatibility
interface OfflineDB {
  data: {
    key: string
    value: {
      id: string
      type: string
      data: Record<string, unknown>
      timestamp: number
    }
  }
  files: {
    key: number
    value: {
      id?: number
      name: string
      file: File
      status: string
      timestamp: number
    }
  }
}

// Legacy class for existing offline storage functionality
class LegacyOfflineStorageService {
  private db: IDBPDatabase<OfflineDB> | null = null

  async init(): Promise<void> {
    this.db = await openDB<OfflineDB>('offline-app', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('data')) {
          db.createObjectStore('data', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('files')) {
          db.createObjectStore('files', { keyPath: 'id', autoIncrement: true })
        }
      }
    })
  }

  // File operations
  async saveFile(file: File): Promise<void> {
    if (!this.db) await this.init()
    const tx = this.db!.transaction('files', 'readwrite')
    await tx.store.add({
      name: file.name,
      file,
      status: 'pending',
      timestamp: Date.now()
    })
    await tx.done
  }

  async getFiles(): Promise<Array<{ id?: number; name: string; file: File; status: string; timestamp: number }>> {
    if (!this.db) await this.init()
    return await this.db!.getAll('files')
  }

  async updateFileStatus(id: number, status: string): Promise<void> {
    if (!this.db) await this.init()
    const tx = this.db!.transaction('files', 'readwrite')
    const file = await tx.store.get(id)
    if (file) {
      file.status = status
      await tx.store.put(file)
    }
    await tx.done
  }

  async deleteFile(id: number): Promise<void> {
    if (!this.db) await this.init()
    const tx = this.db!.transaction('files', 'readwrite')
    await tx.store.delete(id)
    await tx.done
  }

  // Data operations
  async saveData(id: string, type: string, data: Record<string, unknown>): Promise<void> {
    if (!this.db) await this.init()
    const tx = this.db!.transaction('data', 'readwrite')
    await tx.store.put({
      id,
      type,
      data,
      timestamp: Date.now()
    })
    await tx.done
  }

  async getData(id: string): Promise<{ id: string; type: string; data: Record<string, unknown>; timestamp: number } | undefined> {
    if (!this.db) await this.init()
    return await this.db!.get('data', id)
  }

  async deleteData(id: string): Promise<void> {
    if (!this.db) await this.init()
    const tx = this.db!.transaction('data', 'readwrite')
    await tx.store.delete(id)
    await tx.done
  }
}

// Export both services
export const offlineStorage = new LegacyOfflineStorageService()
export const workOrderOfflineStorage = new OfflineStorageService()

// Auto-initialize 
offlineStorage.init().catch(console.error)
workOrderOfflineStorage.initWorkOrderDB().catch(console.error)

// Add global debug helper in development
if (process.env.NODE_ENV === 'development') {
  ((window as unknown) as Record<string, unknown>).debugOfflineStorage = {
    getStats: () => workOrderOfflineStorage.getStorageStats(),
    clearAll: async () => {
      console.warn('🗑️ Clearing all offline storage...')
      await workOrderOfflineStorage.clearDatabase()
    },
    clearWorkOrder: async (workOrderId: number) => {
      console.warn(`🗑️ Clearing work order ${workOrderId} data...`)
      await workOrderOfflineStorage.clearWorkOrderData(workOrderId)
    },
    info: () => 'Use debugOfflineStorage.getStats() to check storage, debugOfflineStorage.clearAll() to reset all, or debugOfflineStorage.clearWorkOrder(id) to clear specific work order'
  }
  
}
